import * as Sentry from '@sentry/vue'
import { isAxiosError } from 'axios'

const ENV = window?.location?.hostname === 'app.thunderstock.nl' ? 'production' : 'development'
// If we are on a local environment, we don't want to send data to Sentry
const isEnabled = window?.location?.hostname !== 'localhost' && ENV === 'production'

/** Statuses `onResponseError` already handles: refresh + replay, login redirect, access-denied dialog. */
const HANDLED_STATUSES = [401, 403, 422]
/** Transport failures that mean the user went offline or left the page, not a defect. */
const EXPECTED_CODES = ['ERR_NETWORK', 'ECONNABORTED', 'ERR_CANCELED']

/**
 * The axios interceptor re-rejects after handling an error, so call sites without a
 * `.catch` turn an expected response into an unhandled rejection. Those carry no
 * information Sentry can act on. 400 and 500 stay reported — they are real failures.
 */
export function isExpectedNetworkError (error) {
  if (!isAxiosError(error)) {
    return false
  }
  const status = error.response?.status
  return status ? HANDLED_STATUSES.includes(status) : EXPECTED_CODES.includes(error.code)
}

export function initSentry (app, router) {
  Sentry.init({
    app,
    logErrors: true,
    trackComponents: true,
    environment: ENV,
    enabled: isEnabled,
    dsn: 'https://917ba9bb36067e37c7c97f2c19e27d29@o477311.ingest.us.sentry.io/4507893799911424',
    integrations: [
      Sentry.browserTracingIntegration({
        router: router,
        routeLabel: 'path',
      }),
    ],
    ignoreErrors: [
      // Duplicate navigation is a no-op the router reports as an error; never a failure.
      /Avoided redundant navigation/,
      // A tab held open across a deploy asks for chunks the new build does not have.
      // `preloadRecovery` reloads once for this, so the event carries no signal.
      /Failed to fetch dynamically imported module/,
      /Importing a module script failed/,
      // Microsoft Outlook SafeLinks / Defender link scanner, not a real user session.
      /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    ],
    beforeSend (event, hint) {
      return isExpectedNetworkError(hint?.originalException) ? null : event
    },
    tracesSampleRate: 0.05, //  Capture 5% of transactions
    maxBreadcrumbs: 15, // The maximum number of breadcrumbs to capture
    normalizeDepth: 5, // The maximum number of frames to be normalized
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      'localhost',
      'https://app.thunderstock.nl',
      'https://staging--thunderstock.netlify.app',
      'https://rj5j2bgjs2.execute-api.eu-west-1.amazonaws.com',
      'https://gfpxhebbti.execute-api.eu-west-1.amazonaws.com',
      'https://r4xayb2z67.execute-api.eu-west-1.amazonaws.com',
      'https://n7mvmu03tg.execute-api.eu-west-1.amazonaws.com',
      'https://il9mo0v0we.execute-api.eu-west-1.amazonaws.com',
      'https://3r1i7yk969.execute-api.eu-west-1.amazonaws.com',
      'https://v1igyiow0a.execute-api.eu-west-1.amazonaws.com',
    ],
    // Profiling
    denyUrls: [
      'https://api.segment.io/v1/m',
      // Chrome extensions
      /extensions\//i,
      /^moz-extension:\/\//i,
      /^chrome:\/\//i,
    ],
  })
}
